import { useCallback, useRef, useState, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ActionsRow from '@lib/components/ActionsRow/ActionsRow';
import { IconName } from '@lib/components/Modal/enums';
import styles from './ConfirmationModal.module.scss';

const {
  VITE_CONFIRMATION_PROGRESS_SECONDS: confirmationProgressSeconds,
  // @ts-ignore
} = import.meta.env;

const PROGRESS_DURATION = Number(confirmationProgressSeconds) || 3;

interface ConfirmationModalProps {
  cancelButtonLabel?: string;
  children?: ReactElement;
  confirmButtonLabel: string;
  description: ReactNode;
  icon: IconName;
  isOpen: boolean;
  loading?: boolean;
  modalBodyClassName?: string;
  modalContentClassName?: string;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  useProgress?: boolean;
}

function ConfirmationModal({
  cancelButtonLabel,
  children,
  confirmButtonLabel,
  description,
  icon,
  isOpen,
  loading,
  modalBodyClassName,
  modalContentClassName,
  onClose,
  onConfirm,
  title,
  useProgress,
}: ConfirmationModalProps) {
  const { t } = useTranslation();

  const prevIsOpenRef = useRef(isOpen);
  const [isProgressActive, setIsProgressActive] = useState(false);

  if (prevIsOpenRef.current !== isOpen) {
    prevIsOpenRef.current = isOpen;
    if (isOpen && useProgress) {
      setIsProgressActive(true);
    } else {
      setIsProgressActive(false);
    }
  }

  const handleProgressComplete = useCallback(() => {
    setIsProgressActive(false);
  }, []);

  const actionsArray = [
    <Button
      onClick={onClose}
      buttonType={ButtonTypes.secondaryOutlined}
      buttonText={cancelButtonLabel || t('cancel')}
      leftIcon="close"
      fullWidth
      data-test-id="confirmation-modal__cancel-button"
    />,
    <Button
      onClick={onConfirm}
      buttonType={ButtonTypes.primaryFilled}
      buttonText={confirmButtonLabel}
      leftIcon="check"
      fullWidth
      isLoading={loading}
      progressDuration={useProgress ? PROGRESS_DURATION : undefined}
      isProgressActive={isProgressActive}
      onProgressComplete={handleProgressComplete}
      data-test-id="confirmation-modal__confirm-button"
    />,
  ];
  const actions = (
    <ActionsRow actions={actionsArray} className={styles.actions} />
  );
  return (
    <Modal
      actions={actions}
      description={description}
      icon={icon}
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      modalBodyClassName={modalBodyClassName}
      modalContentClassName={modalContentClassName}
    >
      {children}
    </Modal>
  );
}

export default ConfirmationModal;
